// LimbSystem.ts: Comprehensive limb development and control system for NeuroPsyMap creatures
// Integrates with existing neural network architecture for organic limb evolution

import { Node, Edge } from './types';
import { NeuralNetworkSimulator } from './NeuralNetworkSimulator';
import { LimbPhysicsEngine } from './limbPhysics';
import { LimbControlSystem } from './limbControl';
import { LimbEvolutionEngine } from './limbEvolution';
import { LifeBehaviorMathEngine, GrowthParameters, CoupledOscillatorState } from './lifeBehaviorMath';
import { AdvancedGrowthEngine } from './advancedGrowthModels';

// Limb type definitions based on the 12 logical realities
export type LimbType = 
    | 'fractal_branch'      // Fractal dimension-based limbs
    | 'psychic_manifest'    // Psychic potential limbs
    | 'linguistic_appendage' // Language-driven limbs
    | 'wave_resonance'      // Wave pattern limbs
    | 'energy_conduit'      // Energy flow limbs
    | 'sensing_probe'       // Sensory limbs
    | 'vocal_modifier'      // Voice synthesis limbs
    | 'neural_extension'    // Network topology limbs
    | 'defensive_spike'     // Evolutionary pressure limbs
    | 'mystical_tendril'    // Psychonaut influence limbs
    | 'temporal_phase'      // Time-based limbs
    | 'adaptive_structure'; // Environment-responsive limbs

export type LimbState = 'dormant' | 'growing' | 'active' | 'evolving' | 'atrophying' | 'regenerating';

export interface LimbSegment {
    id: number;
    x: number;
    y: number;
    z: number;
    length: number;
    angle: number;
    thickness: number;
    energy: number;
    flexibility: number;
    strength: number;
    color: string;
    parentSegment?: number;
    childSegments: number[];
    vx?: number;
    vy?: number;
    vz?: number;
}

export interface Limb {
    id: number;
    type: LimbType;
    state: LimbState;
    rootNodeId: number; // Which neural node this limb grows from
    segments: LimbSegment[];
    totalLength: number;
    energyCapacity: number;
    currentEnergy: number;
    controlNodes: number[]; // Neural nodes that control this limb
    purpose: string;
    evolutionStep: number;
    lastActivity: number;
    effectiveness: number; // How well this limb serves its purpose
    adaptability: number; // How quickly it can evolve
    resonance: number; // Harmonic resonance with creature's base frequency
}

export interface LimbPhysics {
    gravity: number;
    elasticity: number;
    damping: number;
    inertia: number;
    friction: number;
    buoyancy: number;
}

export interface LimbControl {
    targetX: number;
    targetY: number;
    targetZ: number;
    movementSpeed: number;
    precision: number;
    coordination: number; // How well multiple limbs work together
    autonomy: number; // How independently the limb can act
}

export class LimbSystem {
    private network: NeuralNetworkSimulator;
    private mathEngine: LifeBehaviorMathEngine; // Mathematical models for life behaviors
    private limbs: Map<number, Limb> = new Map();
    private limbCounter: number = 0;
    private physics: LimbPhysics;
    private control: LimbControl;
    private physicsEngine: LimbPhysicsEngine;
    private controlSystem: LimbControlSystem;
    private evolutionEngine: LimbEvolutionEngine;
    private advancedGrowthEngine: AdvancedGrowthEngine;
    
    // MATHEMATICAL MODEL: Coupled oscillators for limb coordination
    private limbCoordinationOscillator: CoupledOscillatorState | null = null;
    
    // Limb development triggers
    private developmentTriggers: Map<string, () => boolean> = new Map();
    private growthPatterns: Map<LimbType, (rootNode: Node) => Limb> = new Map();
    
    constructor(network: NeuralNetworkSimulator) {
        this.network = network;
        this.mathEngine = new LifeBehaviorMathEngine(); // Initialize mathematical models
        this.physics = this.initializePhysics();
        this.control = this.initializeControl();
        this.physicsEngine = new LimbPhysicsEngine(network);
        this.controlSystem = new LimbControlSystem(network);
        this.evolutionEngine = new LimbEvolutionEngine(network);
        this.advancedGrowthEngine = new AdvancedGrowthEngine(network);
        this.setupDevelopmentTriggers();
        this.setupGrowthPatterns();
    }

    private initializePhysics(): LimbPhysics {
        return {
            gravity: 0.1,
            elasticity: 0.8,
            damping: 0.9,
            inertia: 0.7,
            friction: 0.3,
            buoyancy: 0.05
        };
    }

    private initializeControl(): LimbControl {
        return {
            targetX: 0,
            targetY: 0,
            targetZ: 0,
            movementSpeed: 1.0,
            precision: 0.8,
            coordination: 0.6,
            autonomy: 0.4
        };
    }

    private setupDevelopmentTriggers(): void {
        // Fractal dimension trigger
        this.developmentTriggers.set('fractal_branch', () => {
            const fractalDim = this.calculateFractalDimension();
            return fractalDim > 1.5 && this.network.nodes.length > 50;
        });

        // Psychic potential trigger
        this.developmentTriggers.set('psychic_manifest', () => {
            const psychicLevel = this.calculatePsychicPotential();
            return psychicLevel > 0.7 && this.network.linguisticComplexity > 3;
        });

        // Linguistic development trigger
        this.developmentTriggers.set('linguistic_appendage', () => {
            return this.network.discoveredWords.length > 5 && 
                   this.network.linguisticComplexity > 2;
        });

        // Wave resonance trigger
        this.developmentTriggers.set('wave_resonance', () => {
            const waveIntensity = this.calculateWaveIntensity();
            return waveIntensity > 0.6 && this.network.wavePatterns.length > 10;
        });

        // Energy flow trigger
        this.developmentTriggers.set('energy_conduit', () => {
            const energyFlow = this.calculateEnergyFlow();
            return energyFlow > 0.8 && this.network.totalNetworkEnergy > 1000;
        });

        // Sensing trigger
        this.developmentTriggers.set('sensing_probe', () => {
            return this.network.creatureState === 'sensing' || 
                   this.network.creatureState === 'exploring';
        });

        // Vocal modification trigger
        this.developmentTriggers.set('vocal_modifier', () => {
            // Allow a voice limb when breathing unlocks voice, or language grows
            const breathingUnlocked = (this.network.breathingSystem?.getBreathingState?.()?.voiceUnlocked) === true;
            return breathingUnlocked || (this.network.linguisticComplexity > 4 && 
                   this.network.successfulCommunications > 10);
        });

        // Neural extension trigger
        this.developmentTriggers.set('neural_extension', () => {
            const connectivity = this.calculateNetworkConnectivity();
            return connectivity > 0.6 && this.network.nodes.length > 100;
        });

        // Defensive trigger
        this.developmentTriggers.set('defensive_spike', () => {
            return this.network.creatureState === 'stabilizing' && 
                   this.network.totalNetworkEnergy < 500;
        });

        // Mystical trigger
        this.developmentTriggers.set('mystical_tendril', () => {
            const psychonautInfluence = this.calculatePsychonautInfluence();
            return psychonautInfluence > 0.5;
        });

        // Temporal trigger
        this.developmentTriggers.set('temporal_phase', () => {
            return this.network.step % 1000 === 0 && this.network.nodes.length > 200;
        });

        // Adaptive trigger
        this.developmentTriggers.set('adaptive_structure', () => {
            return this.network.creatureState === 'exploring' && 
                   this.network.sensingRadius > 300;
        });
    }

    private setupGrowthPatterns(): void {
        // Fractal branch growth
        this.growthPatterns.set('fractal_branch', (rootNode: Node) => {
            return this.createFractalLimb(rootNode);
        });

        // Psychic manifestation growth
        this.growthPatterns.set('psychic_manifest', (rootNode: Node) => {
            return this.createPsychicLimb(rootNode);
        });

        // Linguistic appendage growth
        this.growthPatterns.set('linguistic_appendage', (rootNode: Node) => {
            return this.createLinguisticLimb(rootNode);
        });

        // Wave resonance growth
        this.growthPatterns.set('wave_resonance', (rootNode: Node) => {
            return this.createWaveLimb(rootNode);
        });

        // Energy conduit growth
        this.growthPatterns.set('energy_conduit', (rootNode: Node) => {
            return this.createEnergyLimb(rootNode);
        });

        // Sensing probe growth
        this.growthPatterns.set('sensing_probe', (rootNode: Node) => {
            return this.createSensingLimb(rootNode);
        });

        // Vocal modifier growth
        this.growthPatterns.set('vocal_modifier', (rootNode: Node) => {
            return this.createVocalLimb(rootNode);
        });

        // Neural extension growth
        this.growthPatterns.set('neural_extension', (rootNode: Node) => {
            return this.createNeuralLimb(rootNode);
        });

        // Defensive spike growth
        this.growthPatterns.set('defensive_spike', (rootNode: Node) => {
            return this.createDefensiveLimb(rootNode);
        });

        // Mystical tendril growth
        this.growthPatterns.set('mystical_tendril', (rootNode: Node) => {
            return this.createMysticalLimb(rootNode);
        });

        // Temporal phase growth
        this.growthPatterns.set('temporal_phase', (rootNode: Node) => {
            return this.createTemporalLimb(rootNode);
        });

        // Adaptive structure growth
        this.growthPatterns.set('adaptive_structure', (rootNode: Node) => {
            return this.createAdaptiveLimb(rootNode);
        });
    }

    // Main limb development process
    public developLimbs(): void {
        for (const [triggerName, trigger] of this.developmentTriggers) {
            if (trigger()) {
                const limbType = triggerName as LimbType;
                const suitableNodes = this.findSuitableNodes(limbType);
                
                for (const node of suitableNodes) {
                    if (this.canGrowLimb(node, limbType)) {
                        this.growLimb(node, limbType);
                    }
                }
            }
        }
    }

    // Update all limbs
    public updateLimbs(): void {
        const limbs = Array.from(this.limbs.values());
        
        // Update advanced growth models (chemotaxis, frontier exploration, etc.)
        this.advancedGrowthEngine.updateGrowthModels(0.016);
        
        // MATHEMATICAL MODEL: Update coupled oscillators for limb coordination
        this.updateLimbCoordination(limbs);
        
        // Update control system
        this.controlSystem.updateLimbControl(limbs);
        
        // Update evolution system
        this.evolutionEngine.updateLimbEvolution(limbs);
        
        // Update each limb
        for (const [limbId, limb] of this.limbs) {
            this.physicsEngine.updateLimbPhysics(limb);
            this.updateLimbControl(limb);
            this.updateLimbEvolution(limb);
        }
    }
    
    private updateLimbCoordination(limbs: Limb[]): void {
        if (limbs.length < 2) return; // Need at least 2 limbs for coordination
        
        // Initialize or update coupled oscillator state
        if (!this.limbCoordinationOscillator) {
            // Create initial oscillator state with random phases
            const phases = limbs.map(() => Math.random() * 2 * Math.PI);
            const frequencies = limbs.map(() => 0.5 + Math.random() * 0.5); // Natural frequencies
            
            this.limbCoordinationOscillator = {
                phases: phases,
                frequencies: frequencies,
                couplingStrength: 0.3 // Moderate coupling for coordination
            };
        }
        
        // Calculate Kuramoto dynamics for synchronization
        const dt = 0.016; // 60fps
        this.limbCoordinationOscillator = this.mathEngine.coupledOscillatorModel.calculateKuramotoDynamics(
            this.limbCoordinationOscillator,
            dt
        );
        
        // Calculate synchronization order parameter
        const syncInfo = this.mathEngine.coupledOscillatorModel.calculateOrderParameter(
            this.limbCoordinationOscillator.phases
        );
        
        // Apply synchronization to limb coordination
        for (let i = 0; i < limbs.length; i++) {
            limbs[i].effectiveness = Math.min(1, limbs[i].effectiveness + syncInfo.r * 0.1);
            
            // Use phase information for synchronized movements
            // Higher syncInfo.r means better coordination
        }
    }

    // Create specific limb types
    private createFractalLimb(rootNode: Node): Limb {
        const limbId = this.limbCounter++;
        const segments = this.generateFractalSegments(rootNode, 3);
        
        return {
            id: limbId,
            type: 'fractal_branch',
            state: 'growing',
            rootNodeId: rootNode.id,
            segments,
            totalLength: segments.reduce((sum, seg) => sum + seg.length, 0),
            energyCapacity: 100,
            currentEnergy: 50,
            controlNodes: [rootNode.id],
            purpose: 'Fractal exploration and pattern generation',
            evolutionStep: 0,
            lastActivity: this.network.step,
            effectiveness: 0.7,
            adaptability: 0.8,
            resonance: this.calculateLimbResonance(segments)
        };
    }

    private createPsychicLimb(rootNode: Node): Limb {
        const limbId = this.limbCounter++;
        const segments = this.generatePsychicSegments(rootNode, 2);
        
        return {
            id: limbId,
            type: 'psychic_manifest',
            state: 'growing',
            rootNodeId: rootNode.id,
            segments,
            totalLength: segments.reduce((sum, seg) => sum + seg.length, 0),
            energyCapacity: 150,
            currentEnergy: 75,
            controlNodes: [rootNode.id],
            purpose: 'Psychic energy manipulation and telekinetic interaction',
            evolutionStep: 0,
            lastActivity: this.network.step,
            effectiveness: 0.9,
            adaptability: 0.6,
            resonance: this.calculateLimbResonance(segments)
        };
    }

    private createLinguisticLimb(rootNode: Node): Limb {
        const limbId = this.limbCounter++;
        const segments = this.generateLinguisticSegments(rootNode, 4);
        
        return {
            id: limbId,
            type: 'linguistic_appendage',
            state: 'growing',
            rootNodeId: rootNode.id,
            segments,
            totalLength: segments.reduce((sum, seg) => sum + seg.length, 0),
            energyCapacity: 80,
            currentEnergy: 40,
            controlNodes: [rootNode.id],
            purpose: 'Linguistic expression and communication enhancement',
            evolutionStep: 0,
            lastActivity: this.network.step,
            effectiveness: 0.8,
            adaptability: 0.9,
            resonance: this.calculateLimbResonance(segments)
        };
    }

    // Additional limb creation methods would follow similar patterns...
    private createWaveLimb(rootNode: Node): Limb {
        return this.createGenericLimb(rootNode, 'wave_resonance', 'Wave pattern manipulation and resonance amplification');
    }

    private createEnergyLimb(rootNode: Node): Limb {
        return this.createGenericLimb(rootNode, 'energy_conduit', 'Energy channeling and distribution');
    }

    private createSensingLimb(rootNode: Node): Limb {
        return this.createGenericLimb(rootNode, 'sensing_probe', 'Environmental sensing and exploration');
    }

    private createVocalLimb(rootNode: Node): Limb {
        return this.createGenericLimb(rootNode, 'vocal_modifier', 'Voice synthesis enhancement and modulation');
    }

    private createNeuralLimb(rootNode: Node): Limb {
        return this.createGenericLimb(rootNode, 'neural_extension', 'Neural network expansion and connectivity');
    }

    private createDefensiveLimb(rootNode: Node): Limb {
        return this.createGenericLimb(rootNode, 'defensive_spike', 'Protection and threat response');
    }

    private createMysticalLimb(rootNode: Node): Limb {
        return this.createGenericLimb(rootNode, 'mystical_tendril', 'Mystical energy manipulation and phase shifting');
    }

    private createTemporalLimb(rootNode: Node): Limb {
        return this.createGenericLimb(rootNode, 'temporal_phase', 'Temporal manipulation and phase control');
    }

    private createAdaptiveLimb(rootNode: Node): Limb {
        return this.createGenericLimb(rootNode, 'adaptive_structure', 'Environmental adaptation and response');
    }

    private createGenericLimb(rootNode: Node, type: LimbType, purpose: string): Limb {
        const limbId = this.limbCounter++;
        const segments = this.generateGenericSegments(rootNode, 2);
        
        return {
            id: limbId,
            type,
            state: 'growing',
            rootNodeId: rootNode.id,
            segments,
            totalLength: segments.reduce((sum, seg) => sum + seg.length, 0),
            energyCapacity: 100,
            currentEnergy: 50,
            controlNodes: [rootNode.id],
            purpose,
            evolutionStep: 0,
            lastActivity: this.network.step,
            effectiveness: 0.7,
            adaptability: 0.7,
            resonance: this.calculateLimbResonance(segments)
        };
    }

    // Helper methods for limb creation and management
    private generateFractalSegments(rootNode: Node, count: number): LimbSegment[] {
        const segments: LimbSegment[] = [];
        const goldenRatio = 1.618;
        
        // First segment connects to root node
        let currentX = rootNode.x;
        let currentY = rootNode.y;
        let currentZ = rootNode.z || 0;
        let currentAngle = Math.random() * Math.PI * 2;
        
        for (let i = 0; i < count; i++) {
            const length = 30 * Math.pow(1/goldenRatio, i) * (0.9 + Math.random() * 0.2); // Decreasing size
            
            // Calculate angle with some variation
            currentAngle += (Math.random() - 0.5) * 0.6;
            
            // Position next segment
            const x = currentX + Math.cos(currentAngle) * length;
            const y = currentY + Math.sin(currentAngle) * length;
            const z = currentZ + (Math.random() - 0.5) * 5;
            
            const segment = {
                id: this.limbCounter++,
                x, y, z,
                length,
                angle: currentAngle,
                thickness: 4 - i * 0.4,
                energy: 50 + Math.random() * 50,
                flexibility: 0.8 - i * 0.08,
                strength: 0.6 + i * 0.08,
                color: `hsl(${(rootNode.value * 360 + i * 60) % 360}, 70%, 60%)`,
                parentSegment: i > 0 ? segments[i - 1].id : undefined,
                childSegments: []
            };
            
            segments.push(segment);
            
            // Update position for next segment
            currentX = x;
            currentY = y;
            currentZ = z;
        }
        
        return segments;
    }

    private generatePsychicSegments(rootNode: Node, count: number): LimbSegment[] {
        const segments: LimbSegment[] = [];
        
        let currentX = rootNode.x;
        let currentY = rootNode.y;
        let currentZ = rootNode.z || 0;
        let currentAngle = Math.random() * Math.PI * 2;
        
        for (let i = 0; i < count; i++) {
            const length = 35 * (0.85 + Math.random() * 0.3);
            currentAngle += (Math.random() - 0.5) * 1.0; // More variation for psychic limbs
            
            const x = currentX + Math.cos(currentAngle) * length;
            const y = currentY + Math.sin(currentAngle) * length;
            const z = currentZ + (Math.random() - 0.5) * 10;
            
            const segment = {
                id: this.limbCounter++,
                x, y, z,
                length,
                angle: currentAngle,
                thickness: 2.5,
                energy: 80 + Math.random() * 40,
                flexibility: 0.9,
                strength: 0.4,
                color: `hsl(${(280 + i * 30) % 360}, 90%, 70%)`,
                parentSegment: i > 0 ? segments[i - 1].id : undefined,
                childSegments: []
            };
            
            segments.push(segment);
            
            currentX = x;
            currentY = y;
            currentZ = z;
        }
        
        return segments;
    }

    private generateLinguisticSegments(rootNode: Node, count: number): LimbSegment[] {
        const segments: LimbSegment[] = [];
        
        let currentX = rootNode.x;
        let currentY = rootNode.y;
        let currentZ = rootNode.z || 0;
        let currentAngle = Math.random() * Math.PI * 2;
        
        for (let i = 0; i < count; i++) {
            const length = 28 * (0.9 + Math.random() * 0.2);
            currentAngle += (Math.random() - 0.5) * 0.5; // Moderate variation
            
            const x = currentX + Math.cos(currentAngle) * length;
            const y = currentY + Math.sin(currentAngle) * length;
            const z = currentZ + (Math.random() - 0.5) * 5;
            
            const segment = {
                id: this.limbCounter++,
                x, y, z,
                length,
                angle: currentAngle,
                thickness: 3 - i * 0.3,
                energy: 60 + Math.random() * 30,
                flexibility: 0.7,
                strength: 0.5,
                color: `hsl(${(120 + i * 45) % 360}, 80%, 65%)`,
                parentSegment: i > 0 ? segments[i - 1].id : undefined,
                childSegments: []
            };
            
            segments.push(segment);
            
            currentX = x;
            currentY = y;
            currentZ = z;
        }
        
        return segments;
    }

    private generateGenericSegments(rootNode: Node, count: number): LimbSegment[] {
        const segments: LimbSegment[] = [];
        
        let currentX = rootNode.x;
        let currentY = rootNode.y;
        let currentZ = rootNode.z || 0;
        let currentAngle = Math.random() * Math.PI * 2;
        
        // Create a temporary limb to use with advanced growth engine
        const tempLimb = this.createGenericLimb(rootNode, 'neural_extension', 'Exploration limb');
        
        for (let i = 0; i < count; i++) {
            const length = 25 * (0.85 + Math.random() * 0.3);
            
            // Use advanced growth engine to determine direction
            const growthDirection = this.advancedGrowthEngine.getNextGrowthDirection(tempLimb);
            currentAngle = Math.atan2(growthDirection.y, growthDirection.x);
            
            const x = currentX + Math.cos(currentAngle) * length;
            const y = currentY + Math.sin(currentAngle) * length;
            const z = currentZ + growthDirection.z * length;
            
            const segment = {
                id: this.limbCounter++,
                x, y, z,
                length,
                angle: currentAngle,
                thickness: 3 - i * 0.3,
                energy: 50 + Math.random() * 40,
                flexibility: 0.6,
                strength: 0.6,
                color: `hsl(${(rootNode.value * 360 + i * 30) % 360}, 70%, 60%)`,
                parentSegment: i > 0 ? segments[i - 1].id : undefined,
                childSegments: []
            };
            
            segments.push(segment);
            
            // Update tempLimb for next iteration
            tempLimb.segments.push(segment);
            
            currentX = x;
            currentY = y;
            currentZ = z;
        }
        
        return segments;
    }

    // Calculation methods
    private calculateFractalDimension(): number {
        // Simplified fractal dimension calculation
        return Math.log(this.network.nodes.length) / Math.log(10);
    }

    private calculatePsychicPotential(): number {
        // Calculate based on linguistic complexity and energy levels
        const energyFactor = this.network.totalNetworkEnergy / 1000;
        const linguisticFactor = this.network.linguisticComplexity / 10;
        return (energyFactor + linguisticFactor) / 2;
    }

    private calculateWaveIntensity(): number {
        return this.network.wavePatterns.reduce((sum, pattern) => sum + Math.abs(pattern), 0) / 
               Math.max(1, this.network.wavePatterns.length);
    }

    private calculateEnergyFlow(): number {
        return this.network.edges.reduce((sum, edge) => sum + edge.weight, 0) / 
               Math.max(1, this.network.edges.length);
    }

    private calculateNetworkConnectivity(): number {
        return this.network.edges.length / Math.max(1, this.network.nodes.length);
    }

    private calculatePsychonautInfluence(): number {
        const distance = Math.sqrt(
            Math.pow(this.network.psychonautCreature.x - 400, 2) + 
            Math.pow(this.network.psychonautCreature.y - 400, 2)
        );
        return Math.max(0, 1 - distance / this.network.psychonautCreature.influenceRadius);
    }

    private calculateLimbResonance(segments: LimbSegment[]): number {
        return segments.reduce((sum, seg) => sum + seg.energy, 0) / 
               Math.max(1, segments.length) / 100;
    }

    // Utility methods
    private findSuitableNodes(limbType: LimbType): Node[] {
        // Find nodes that are suitable for growing specific limb types
        return this.network.nodes.filter(node => {
            const connections = this.network.edges.filter(e => e.from === node.id || e.to === node.id).length;
            return connections > 2 && (node.energy || 0) > 0.5;
        });
    }

    private canGrowLimb(node: Node, limbType: LimbType): boolean {
        // Check if a node can grow a specific type of limb
        const existingLimbs = Array.from(this.limbs.values()).filter(limb => limb.rootNodeId === node.id);
        return existingLimbs.length < 3; // Limit limbs per node
    }

    private growLimb(node: Node, limbType: LimbType): void {
        const growthPattern = this.growthPatterns.get(limbType);
        if (growthPattern) {
            const limb = growthPattern(node);
            this.limbs.set(limb.id, limb);
            console.log(`[Limb System] Grew ${limbType} limb from node ${node.id}`);
        }
    }

    // Physics is now handled by the physics engine

    private updateLimbControl(limb: Limb): void {
        // Update limb control based on neural network activity
        const controlNode = this.network.nodes.find(n => n.id === limb.rootNodeId);
        if (controlNode) {
            // Move limb based on neural activity
            const activity = controlNode.energy || 0;
            if (activity > 0.7) {
                limb.state = 'active';
            } else if (activity < 0.3) {
                limb.state = 'dormant';
            }
        }
    }

    private updateLimbEvolution(limb: Limb): void {
        // Update limb evolution based on effectiveness and usage
        if (limb.effectiveness > 0.8 && limb.currentEnergy > limb.energyCapacity * 0.7) {
            limb.evolutionStep++;
            limb.adaptability = Math.min(1, limb.adaptability + 0.01);
        }
    }

    // Public interface methods
    public getLimbs(): Limb[] {
        return Array.from(this.limbs.values());
    }

    public getLimbById(id: number): Limb | undefined {
        return this.limbs.get(id);
    }

    public getLimbsByType(type: LimbType): Limb[] {
        return Array.from(this.limbs.values()).filter(limb => limb.type === type);
    }

    public removeLimb(id: number): boolean {
        return this.limbs.delete(id);
    }

    public getLimbCount(): number {
        return this.limbs.size;
    }

    public getTotalLimbLength(): number {
        return Array.from(this.limbs.values()).reduce((sum, limb) => sum + limb.totalLength, 0);
    }
    
    // Physics engine access methods
    public getPhysicsEngine(): LimbPhysicsEngine {
        return this.physicsEngine;
    }
    
    public addEnvironmentalField(field: any): void {
        this.physicsEngine.addEnvironmentalField(field);
    }
    
    public setPhysicsConstants(constants: any): void {
        this.physicsEngine.setPhysicsConstants(constants);
    }
    
    // Control system access methods
    public getControlSystem(): LimbControlSystem {
        return this.controlSystem;
    }
    
    public getActiveControlSignals(): any[] {
        return this.controlSystem.getActiveSignals();
    }
    
    public getCoordinationPatterns(): any[] {
        return this.controlSystem.getCoordinationPatterns();
    }
    
    public setLearningRate(rate: number): void {
        this.controlSystem.setLearningRate(rate);
    }
    
    public setCoordinationLevel(level: number): void {
        this.controlSystem.setCoordinationLevel(level);
    }
    
    // Evolution engine access methods
    public getEvolutionEngine(): LimbEvolutionEngine {
        return this.evolutionEngine;
    }
    
    public getEvolutionMetrics(): any {
        return this.evolutionEngine.getEvolutionMetrics();
    }
    
    public getLimbGenome(limbId: number): any {
        return this.evolutionEngine.getLimbGenome(limbId);
    }
    
    public getAllGenomes(): any[] {
        return this.evolutionEngine.getAllGenomes();
    }
    
    public addEnvironmentalPressure(pressure: any): void {
        this.evolutionEngine.addEnvironmentalPressure(pressure);
    }
    
    public setMutationRate(rate: number): void {
        this.evolutionEngine.setMutationRate(rate);
    }
    
    public setSelectionPressure(pressure: number): void {
        this.evolutionEngine.setSelectionPressure(pressure);
    }
    
    public forceEvolution(): void {
        this.evolutionEngine.forceEvolution();
    }
}
